<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'

import { listSkus, type Sku } from '../api/skus'
import {
  SCOPE_LABELS,
  STATUS_LABELS,
  STATUS_TAG,
  cancelStocktake,
  createStocktake,
  deleteStocktake,
  listStocktakes,
  startStocktake,
  type ListRow,
  type StocktakeScope,
  type StocktakeStatus,
} from '../api/stocktakes'
import { listWarehouses, type Warehouse } from '../api/warehouses'

const router = useRouter()

const rows = ref<ListRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')
const busy = ref(false)

const warehouses = ref<Warehouse[]>([])
const skus = ref<Sku[]>([])
const refError = ref<unknown>(null)

// 参照下拉用 undefined 表示「未选」：0 会被 el-select 当成值显示成「0」，placeholder 就没了
const query = reactive({
  keyword: '',
  warehouse: undefined as number | undefined,
  status: '',
  page: 1,
  size: 20,
})

/** 新建盘点单：范围三选一，zone / sku_id 只在对应范围下才有意义 */
const creating = ref(false)
const createError = ref<unknown>(null)
const form = reactive({
  warehouse_id: undefined as number | undefined,
  scope: 'all' as StocktakeScope,
  zone: '',
  sku_id: undefined as number | undefined,
  remark: '',
})

function label(id: number, list: { id: number; code: string; name: string }[]): string {
  const hit = list.find((item) => item.id === id)
  return hit ? `${hit.code} ${hit.name}`.trim() : `#${id}`
}

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listStocktakes({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      warehouse: query.warehouse || undefined,
      status: query.status || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function loadRefs(): Promise<void> {
  refError.value = null
  try {
    warehouses.value = (await listWarehouses({ page: 1, size: 100 })).list
  } catch (e) {
    // 参照数据拿不到下拉会空，但必须把原因露出来（如未选货主时后端回 422），否则页面看着像没数据
    refError.value = e
  }
  try {
    skus.value = (await listSkus({ page: 1, size: 200 })).list
  } catch (e) {
    if (!refError.value) refError.value = e
  }
}

onMounted(() => {
  void load()
  void loadRefs()
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function openCreate(): void {
  createError.value = null
  form.warehouse_id = warehouses.value[0]?.id
  form.scope = 'all'
  form.zone = ''
  form.sku_id = undefined
  form.remark = ''
  creating.value = true
}

async function submitCreate(): Promise<void> {
  createError.value = null
  if (!form.warehouse_id) {
    createError.value = '请选择仓库'
    return
  }
  if (form.scope === 'sku' && !form.sku_id) {
    createError.value = '请选择商品'
    return
  }
  busy.value = true
  try {
    const take = await createStocktake({
      warehouse_id: form.warehouse_id,
      scope: form.scope,
      zone: form.scope === 'zone' ? form.zone.trim() : undefined,
      sku_id: form.scope === 'sku' ? form.sku_id : undefined,
      remark: form.remark.trim() || undefined,
    })
    creating.value = false
    await load(`盘点单「${take.doc_no}」已创建，账面快照已展开`)
  } catch (e) {
    createError.value = e
  } finally {
    busy.value = false
  }
}

async function start(row: ListRow): Promise<void> {
  notice.value = ''
  error.value = null
  busy.value = true
  try {
    await startStocktake(row.id)
    await load(`盘点单「${row.doc_no}」已开始，账面按当前库存重新拍了一次`)
  } catch (e) {
    error.value = e
  } finally {
    busy.value = false
  }
}

async function cancel(row: ListRow): Promise<void> {
  notice.value = ''
  error.value = null
  busy.value = true
  try {
    await cancelStocktake(row.id)
    await load(`盘点单「${row.doc_no}」已取消`)
  } catch (e) {
    error.value = e
  } finally {
    busy.value = false
  }
}

async function remove(row: ListRow): Promise<void> {
  notice.value = ''
  error.value = null
  busy.value = true
  try {
    await deleteStocktake(row.id)
    await load(`盘点单「${row.doc_no}」已删除`)
  } catch (e) {
    error.value = e
  } finally {
    busy.value = false
  }
}

function open(row: ListRow): void {
  void router.push({ name: 'stocktake-detail', params: { id: row.id } })
}

/** 范围的可读描述：全仓 / 库区 / 商品编码 */
function scopeText(row: ListRow): string {
  const base = SCOPE_LABELS[row.scope] ?? row.scope
  if (row.scope === 'zone') return `${base} · ${row.zone}`
  if (row.scope === 'sku') {
    const hit = skus.value.find((s) => s.id === row.sku_id)
    return `${base} · ${hit ? `${hit.code} ${hit.name}`.trim() : `#${row.sku_id}`}`
  }
  return base
}
</script>

<template>
  <div class="yc-st">
    <header class="yc-st__bar">
      <el-input
        v-model="query.keyword"
        class="yc-st__keyword"
        placeholder="盘点单号"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.warehouse" class="yc-st__ref" placeholder="全部仓库" clearable @change="search">
        <el-option v-for="w in warehouses" :key="w.id" :label="label(w.id, warehouses)" :value="w.id" />
      </el-select>
      <el-select v-model="query.status" class="yc-st__status" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(text, value) in STATUS_LABELS" :key="value" :label="text" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button @click="load()">刷新</el-button>
      <el-button v-perm="'wms:stocktake:create'" type="primary" @click="openCreate">新建盘点单</el-button>
      <span v-if="notice" class="yc-st__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-st__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-st__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="doc_no" label="盘点单号" width="180" />
        <el-table-column label="仓库" min-width="140">
          <template #default="{ row }">{{ row.warehouse_name || `#${row.warehouse_id}` }}</template>
        </el-table-column>
        <el-table-column label="盘点范围" min-width="160">
          <template #default="{ row }">{{ scopeText(row) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="96">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as StocktakeStatus]" size="small">
              {{ STATUS_LABELS[row.status as StocktakeStatus] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="盘点进度" min-width="170">
          <template #default="{ row }">
            <el-progress
              :percentage="row.item_count ? Math.round((row.counted_count / row.item_count) * 100) : 0"
              :stroke-width="8"
              :status="row.item_count && row.counted_count >= row.item_count ? 'success' : undefined"
            />
            <span class="yc-st__qty">
              {{ row.counted_count }} / {{ row.item_count }} 行已盘
              <template v-if="row.diff_count">，{{ row.diff_count }} 行有差异</template>
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="created_by" label="创建人" width="110" />
        <el-table-column label="创建时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="open(row)">详情</el-button>
            <el-button
              v-if="row.status === 'draft'"
              v-perm="'wms:stocktake:start'"
              :loading="busy"
              link
              type="primary"
              @click="start(row)"
            >
              开始
            </el-button>
            <el-popconfirm
              v-if="row.status === 'draft' || row.status === 'counting'"
              title="确认取消该盘点单？"
              width="260"
              @confirm="cancel(row)"
            >
              <template #reference>
                <el-button v-perm="'wms:stocktake:cancel'" link type="danger">取消</el-button>
              </template>
            </el-popconfirm>
            <el-popconfirm
              v-if="row.status === 'draft'"
              title="确认删除该盘点单？明细会一并删除。"
              width="280"
              @confirm="remove(row)"
            >
              <template #reference>
                <el-button v-perm="'wms:stocktake:delete'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有盘点单"
        description="新建盘点单选好仓库与范围，系统会把范围内的库存拍成账面快照，开始后逐行录实盘数。"
      />
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-st__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog v-model="creating" title="新建盘点单" width="520px">
      <div v-if="createError" class="yc-st__error">
        <TraceIdAlert :error="createError" />
      </div>
      <el-form label-width="96px" @submit.prevent>
        <el-form-item label="仓库" required>
          <el-select v-model="form.warehouse_id" class="yc-st__full" placeholder="请选择仓库">
            <el-option v-for="w in warehouses" :key="w.id" :label="label(w.id, warehouses)" :value="w.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="盘点范围" required>
          <el-radio-group v-model="form.scope">
            <el-radio value="all">全仓</el-radio>
            <el-radio value="zone">按库区</el-radio>
            <el-radio value="sku">指定商品</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="form.scope === 'zone'" label="库区" required>
          <el-input v-model="form.zone" placeholder="如 A、B、冷藏区" />
        </el-form-item>
        <el-form-item v-if="form.scope === 'sku'" label="商品" required>
          <el-select v-model="form.sku_id" class="yc-st__full" filterable placeholder="请选择商品">
            <el-option
              v-for="s in skus"
              :key="s.id"
              :label="`${s.code} ${s.name}`.trim()"
              :value="s.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" :rows="2" placeholder="选填" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="creating = false">取消</el-button>
        <el-button :loading="busy" type="primary" @click="submitCreate">创建</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-st {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 180px;
  }

  &__ref {
    width: 190px;
  }

  &__status {
    width: 140px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__qty {
    display: block;
    margin-top: var(--yc-space-1);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__full {
    width: 100%;
  }
}
</style>
